/* LUME hero light waves (updated 19 Oct 2026 for speed; 21 Oct 2026: works with every version of the hero)
   - When the hero has a video, the waves are drawn once as a still background while the video loads,
     and stop completely once the video plays (they were being redrawn 60 times a second behind it).
     If the video cannot play, the waves move instead.
   - When there is no video, the waves move gently at 30 frames a second, only while the hero is on screen
   - Drawn at a lighter resolution and without the costly blur, so phones stay smooth */
(() => {
  const cv = document.getElementById('heroCanvas');
  if (!cv) return;
  const ctx = cv.getContext('2d');
  if (!ctx) return;

  const KEYS = [
    { rgb: '201,63,46', lambda: 644 },
    { rgb: '63,143,92', lambda: 538 },
    { rgb: '216,155,43', lambda: 580 },
    { rgb: '61,95,168', lambda: 453.5 },
  ];
  const calm =
    (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ||
    document.documentElement.dataset.lumeAnim === 'off';
  const video = document.getElementById('heroVideo');
  const DPR = Math.min(window.devicePixelRatio || 1, 1.5);

  let active = 0;
  let W = 0;
  let H = 0;
  let t = 0;
  let raf = 0;
  let last = 0;
  let visible = true;
  let stopped = false;

  const size = () => {
    W = cv.width = Math.round(cv.offsetWidth * DPR);
    H = cv.height = Math.round(cv.offsetHeight * DPR);
  };

  const draw = () => {
    const p = KEYS[active];
    ctx.fillStyle = '#14211A';
    ctx.fillRect(0, 0, W, H);
    const g = ctx.createRadialGradient(W * 0.72, H * 0.34, 0, W * 0.72, H * 0.34, Math.max(W, H) * 0.75);
    g.addColorStop(0, `rgba(${p.rgb},.16)`);
    g.addColorStop(1, 'rgba(22,40,31,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    KEYS.forEach((pr, i) => {
      const act = i === active;
      const baseY = H * (0.3 + i * 0.16);
      const amp = H * 0.05 * (act ? 1.5 : 1);
      const fr = 900 / pr.lambda;
      ctx.beginPath();
      for (let x = 0; x <= W; x += 8) {
        const y =
          baseY +
          Math.sin((x / W) * fr * Math.PI * 2 + t * (0.4 + i * 0.08)) * amp +
          Math.sin((x / W) * fr * Math.PI * 4.7 + t * 0.9) * amp * 0.3;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      if (act) {
        /* soft glow: a wide faint line under the bright one */
        ctx.strokeStyle = `rgba(${pr.rgb},.22)`;
        ctx.lineWidth = 9 * DPR;
        ctx.stroke();
      }
      ctx.strokeStyle = `rgba(${pr.rgb},${act ? 0.85 : 0.28})`;
      ctx.lineWidth = (act ? 2.6 : 1.4) * DPR;
      ctx.stroke();
    });
  };

  const loop = (now) => {
    if (stopped || !visible) return;
    raf = requestAnimationFrame(loop);
    if (now - last < 33) return; // about 30 frames a second
    last = now;
    t += 0.024;
    draw();
  };

  const stop = () => {
    stopped = true;
    cancelAnimationFrame(raf);
  };

  size();
  draw();
  addEventListener('resize', () => {
    if (stopped) return;
    size();
    draw();
  });

  let moving = false;
  const animate = () => {
    if (calm || moving) return;
    moving = true;
    stopped = false;
    setInterval(() => {
      if (stopped || !visible) return;
      active = (active + 1) % KEYS.length;
    }, 5000);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        visible = e.isIntersecting;
        if (visible && !stopped) {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(loop);
        }
      }, { threshold: 0.05 }).observe(cv);
    } else {
      raf = requestAnimationFrame(loop);
    }
  };

  const hasVideo = !!(video && (video.getAttribute('src') || video.getAttribute('data-src') || video.querySelector('source')));
  if (hasVideo) {
    /* Still frame while the video loads; once it plays, the waves are hidden behind it, so stop.
       If the video fails to play, the waves move instead. */
    if (video.error) { animate(); return; }
    if (!video.paused && video.readyState > 2) { stop(); return; }
    video.addEventListener('playing', stop, { once: true });
    document.addEventListener('lume:hero-video-playing', stop, { once: true });
    video.addEventListener('error', animate, { once: true });
    return;
  }

  animate();
})();